import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * SEO内容组件 - 包含QR Code相关信息、FAQ等，用于SEO优化
 */
const SEOContent = () => {
  const { language } = useLanguage();
  const [openFaqs, setOpenFaqs] = useState<number[]>([]);

  /**
   * 切换FAQ展开/收起状态
   */
  const toggleFaq = (index: number) => {
    setOpenFaqs(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-16">
      {/* QR Code 基础知识 */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-white mb-8 text-center">
          {language === 'zh' ? '二维码基础知识' : "I'm new to QR Codes. What should I know?"}
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <h3 className="text-xl font-semibold text-white mb-4">
              {language === 'zh' ? '什么是二维码？' : 'What are QR Codes?'}
            </h3>
            <p className="text-gray-300 leading-relaxed">
              {language === 'zh' 
                ? '二维码（QR Code）是一种快速响应码，采用二维矩阵条形码技术。QR码生成器可以将网址、文本、联系信息、WiFi密码等内容编码为可扫描的二维码图像。免费QR码生成器支持批量生成、自定义颜色、多种尺寸选择，广泛应用于数字营销、移动支付、无接触服务等商业场景。'
                : 'QR Codes (Quick Response Codes) are matrix barcode technology that stores information in a two-dimensional pattern. QR code generators can encode URLs, text, contact information, WiFi passwords, and more into scannable QR code images. Free QR code generators support bulk generation, custom colors, multiple size options, and are widely used in digital marketing, mobile payments, and contactless services.'
              }
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <h3 className="text-xl font-semibold text-white mb-4">
              {language === 'zh' ? '为什么使用二维码？' : 'Why use QR Codes?'}
            </h3>
            <p className="text-gray-300 leading-relaxed">
              {language === 'zh'
                ? '使用二维码生成器创建的QR码具有扫描速度快、信息容量大、错误纠正能力强的优势。移动设备用户只需用手机摄像头扫描二维码，即可快速访问网站链接、获取产品信息、连接WiFi网络或添加联系人，无需手动输入长串文字。QR码应用于餐厅菜单、营销推广、活动签到、支付收款等场景，显著提升用户体验和业务效率。'
                : 'QR codes created with QR code generators offer fast scanning speed, large information capacity, and strong error correction capabilities. Mobile device users can quickly access website links, get product information, connect to WiFi networks, or add contacts by simply scanning QR codes with their phone camera, eliminating manual text input. QR codes are used in restaurant menus, marketing campaigns, event check-ins, payment collection, and more, significantly improving user experience and business efficiency.'
              }
            </p>
          </div>
        </div>
      </section>

      {/* 如何创建免费QR码 */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-white mb-8 text-center">
          {language === 'zh' ? '如何创建免费二维码？' : 'How do I create a free QR Code?'}
        </h2>
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">1</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {language === 'zh' ? '选择类型' : 'Choose Type'}
              </h3>
              <p className="text-gray-300 text-sm">
                {language === 'zh' 
                  ? '选择你需要的二维码类型，如网址、文本、邮箱等'
                  : 'Select your QR code type: URL, text, email, etc.'
                }
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">2</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {language === 'zh' ? '输入内容' : 'Enter Content'}
              </h3>
              <p className="text-gray-300 text-sm">
                {language === 'zh'
                  ? '在表单中输入相关信息，如网址、文本内容等'
                  : 'Enter your information in the form fields'
                }
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">3</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {language === 'zh' ? '生成下载' : 'Generate & Download'}
              </h3>
              <p className="text-gray-300 text-sm">
                {language === 'zh'
                  ? '点击生成按钮，然后下载或分享你的二维码'
                  : 'Click generate and download or share your QR code'
                }
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 支持的QR码类型 */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-white mb-8 text-center">
          {language === 'zh' ? '可以创建哪些类型的免费二维码？' : 'What type of QR Codes can I create for free?'}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              title: language === 'zh' ? '网址二维码生成器' : 'URL QR Code Generator',
              desc: language === 'zh' ? '快速生成网站链接二维码，支持长网址和短网址' : 'Generate website link QR codes quickly, supporting long and short URLs',
              icon: '🌐'
            },
            {
              title: language === 'zh' ? '文本二维码制作工具' : 'Text QR Code Creator',
              desc: language === 'zh' ? '将纯文本转换为二维码，支持多语言内容' : 'Convert plain text to QR codes with multi-language support',
              icon: '📝'
            },
            {
              title: language === 'zh' ? '邮件二维码生成' : 'Email QR Code Generator',
              desc: language === 'zh' ? '创建邮件二维码，快速发送电子邮件' : 'Create email QR codes for quick email sending',
              icon: '📧'
            },
            {
              title: language === 'zh' ? '电话二维码制作' : 'Phone QR Code Maker',
              desc: language === 'zh' ? '生成电话号码二维码，一键拨打电话' : 'Generate phone number QR codes for one-click calling',
              icon: '📞'
            },
            {
              title: language === 'zh' ? '短信二维码工具' : 'SMS QR Code Tool',
              desc: language === 'zh' ? '制作短信二维码，预设消息内容' : 'Create SMS QR codes with preset message content',
              icon: '💬'
            },
            {
              title: language === 'zh' ? 'WiFi二维码生成器' : 'WiFi QR Code Generator',
              desc: language === 'zh' ? '生成WiFi连接二维码，客人扫码自动连网' : 'Generate WiFi connection QR codes for automatic network access',
              icon: '📶'
            },
            {
              title: language === 'zh' ? 'Facebook二维码制作' : 'Facebook QR Code Creator',
              desc: language === 'zh' ? '创建Facebook个人主页和分享链接二维码' : 'Create QR codes for Facebook profiles and share links',
              icon: '📘'
            },
            {
              title: language === 'zh' ? 'Twitter二维码生成' : 'Twitter QR Code Generator',
              desc: language === 'zh' ? '制作Twitter主页和推文分享二维码' : 'Create QR codes for Twitter profiles and tweet sharing',
              icon: '🐦'
            }
          ].map((type, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-3xl mb-3">{type.icon}</div>
              <h3 className="text-lg font-semibold text-white mb-2">{type.title}</h3>
              <p className="text-gray-300 text-sm">{type.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 行业应用指南 */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-white mb-8 text-center">
          {language === 'zh' ? '行业应用指南' : 'Industry Use Cases'}
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <div className="flex items-center mb-4">
              <div className="text-2xl mr-3">🍽️</div>
              <h3 className="text-xl font-semibold text-white">
                {language === 'zh' ? '餐饮业解决方案' : 'Restaurant Solutions'}
              </h3>
            </div>
            <ul className="text-gray-300 space-y-2 text-sm">
              <li>• {language === 'zh' ? '无接触菜单二维码，提升用餐安全' : 'Contactless menu QR codes for safer dining'}</li>
              <li>• {language === 'zh' ? 'WiFi密码分享，改善客户体验' : 'WiFi password sharing for better customer experience'}</li>
              <li>• {language === 'zh' ? '外卖订单追踪，便捷点餐服务' : 'Takeout order tracking with convenient ordering'}</li>
              <li>• {language === 'zh' ? '客户反馈收集，优化服务质量' : 'Customer feedback collection to improve service quality'}</li>
            </ul>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <div className="flex items-center mb-4">
              <div className="text-2xl mr-3">🏪</div>
              <h3 className="text-xl font-semibold text-white">
                {language === 'zh' ? '零售业营销工具' : 'Retail Marketing Tools'}
              </h3>
            </div>
            <ul className="text-gray-300 space-y-2 text-sm">
              <li>• {language === 'zh' ? '产品信息展示，快速了解详情' : 'Product information display for quick details'}</li>
              <li>• {language === 'zh' ? '促销活动推广，增加销售转化' : 'Promotional campaigns to boost sales conversion'}</li>
              <li>• {language === 'zh' ? '会员注册引导，扩大客户群体' : 'Member registration guidance to expand customer base'}</li>
              <li>• {language === 'zh' ? '社交媒体关注，建立品牌社群' : 'Social media following to build brand community'}</li>
            </ul>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <div className="flex items-center mb-4">
              <div className="text-2xl mr-3">🎓</div>
              <h3 className="text-xl font-semibold text-white">
                {language === 'zh' ? '教育培训应用' : 'Education & Training'}
              </h3>
            </div>
            <ul className="text-gray-300 space-y-2 text-sm">
              <li>• {language === 'zh' ? '课程资料分享，便捷学习资源' : 'Course material sharing for convenient learning resources'}</li>
              <li>• {language === 'zh' ? '在线作业提交，提高教学效率' : 'Online homework submission to improve teaching efficiency'}</li>
              <li>• {language === 'zh' ? '活动签到统计，简化管理流程' : 'Event check-in statistics to simplify management'}</li>
              <li>• {language === 'zh' ? '家校沟通桥梁，加强互动交流' : 'School-home communication bridge for enhanced interaction'}</li>
            </ul>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <div className="flex items-center mb-4">
              <div className="text-2xl mr-3">🏢</div>
              <h3 className="text-xl font-semibold text-white">
                {language === 'zh' ? '企业办公效率' : 'Business Office Efficiency'}
              </h3>
            </div>
            <ul className="text-gray-300 space-y-2 text-sm">
              <li>• {language === 'zh' ? '员工联系信息，快速建立通讯' : 'Employee contact info for quick communication setup'}</li>
              <li>• {language === 'zh' ? '会议室预订系统，优化空间利用' : 'Meeting room booking system for optimal space utilization'}</li>
              <li>• {language === 'zh' ? '文档快速分享，提升协作效率' : 'Quick document sharing to enhance collaboration efficiency'}</li>
              <li>• {language === 'zh' ? '访客管理系统，加强安全控制' : 'Visitor management system for enhanced security control'}</li>
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ部分 */}
      <section>
        <h2 className="text-3xl font-bold text-white mb-8 text-center">
          {language === 'zh' ? '常见问题' : 'Frequently Asked Questions (FAQs)'}
        </h2>
        <div className="space-y-4">
          {[
            {
              q: language === 'zh' ? '二维码生成器是免费的吗？' : 'Is the QR code generator free?',
              a: language === 'zh' 
                ? '是的，我们的二维码生成器完全免费，无需注册，无隐藏费用。支持生成无限数量的高质量二维码。'
                : 'Yes, our QR code generator is completely free to use. No registration required, no hidden fees, and unlimited QR code generation.'
            },
            {
              q: language === 'zh' ? '生成的二维码有使用限制吗？' : 'Are there any usage restrictions for generated QR codes?',
              a: language === 'zh'
                ? '没有限制，你可以将生成的二维码用于个人或商业用途。支持打印、分享和长期使用。'
                : 'No restrictions. You can use the generated QR codes for personal or commercial purposes, including printing, sharing, and long-term use.'
            },
            {
              q: language === 'zh' ? '二维码可以自定义颜色和大小吗？' : 'Can I customize QR code colors and sizes?',
              a: language === 'zh'
                ? '可以，我们支持自定义前景色和背景色，多种尺寸选择，以及不同的错误纠正级别，让你的二维码更有个性。'
                : 'Yes, you can customize foreground and background colors, choose different sizes, and select error correction levels to make your QR codes more personal.'
            },
            {
              q: language === 'zh' ? '如何扫描二维码？' : 'How do I scan QR codes?',
              a: language === 'zh'
                ? '使用手机相机应用或专门的二维码扫描应用即可扫描。大多数现代手机都内置了二维码扫描功能，支持iOS和Android。'
                : 'Use your phone camera app or a dedicated QR code scanner app. Most modern phones have built-in QR code scanning for both iOS and Android.'
            },
            {
              q: language === 'zh' ? '二维码可以包含多少信息？' : 'How much information can QR codes contain?',
              a: language === 'zh'
                ? '标准二维码最多可存储3KB数据，约4296个字符，足够存储长网址、大段文本、联系信息、WiFi密码等。'
                : 'Standard QR codes can store up to 3KB of data (about 4,296 characters), enough for long URLs, large text blocks, contact information, WiFi passwords, and more.'
            },
            {
              q: language === 'zh' ? '二维码会过期吗？' : 'Do QR codes expire?',
              a: language === 'zh'
                ? '静态二维码本身不会过期，可以永久使用。但如果链接的网址失效，扫描后可能无法访问。动态二维码的有效期取决于服务提供商。'
                : 'Static QR codes don\'t expire and can be used permanently. However, if the linked URL becomes invalid, scanning may fail. Dynamic QR code validity depends on the service provider.'
            },
            {
              q: language === 'zh' ? '什么是二维码的最佳尺寸？' : 'What is the best size for QR codes?',
              a: language === 'zh'
                ? '二维码的最佳尺寸取决于使用场景。一般建议：名片上至少2x2cm，海报上至少3x3cm，距离越远尺寸应越大，保证扫描距离是二维码宽度的10倍。'
                : 'The best QR code size depends on usage. Generally recommended: at least 2x2cm for business cards, 3x3cm for posters. The scanning distance should be 10 times the QR code width.'
            },
            {
              q: language === 'zh' ? '为什么我的二维码扫描不了？' : 'Why can\'t my QR code be scanned?',
              a: language === 'zh'
                ? '常见原因：1）尺寸太小 2）颜色对比度不够 3）印刷质量差 4）损坏或污损 5）扫描角度不当。建议使用深色前景+浅色背景，确保足够的对比度。'
                : 'Common reasons: 1) Too small size 2) Insufficient color contrast 3) Poor print quality 4) Damage or dirt 5) Wrong scanning angle. Use dark foreground with light background for better contrast.'
            },
            {
              q: language === 'zh' ? '餐厅菜单二维码怎么制作？' : 'How to create QR codes for restaurant menus?',
              a: language === 'zh'
                ? '制作餐厅菜单二维码：1）准备菜单PDF或网页版本 2）选择URL类型生成二维码 3）测试扫描效果 4）打印在桌贴或桌牌上。建议使用高错误纠正级别以适应餐厅环境。'
                : 'Create restaurant menu QR codes: 1) Prepare menu PDF or webpage 2) Select URL type to generate QR code 3) Test scanning 4) Print on table stickers or stands. Use high error correction for restaurant environments.'
            },
            {
              q: language === 'zh' ? 'WiFi二维码如何生成和使用？' : 'How to generate and use WiFi QR codes?',
              a: language === 'zh'
                ? 'WiFi二维码生成步骤：1）选择WiFi类型 2）输入网络名称(SSID) 3）选择加密类型(WPA/WEP/无加密) 4）输入密码 5）生成并打印。客人扫描后可自动连接WiFi，无需手动输入密码。'
                : 'WiFi QR code steps: 1) Select WiFi type 2) Enter network name (SSID) 3) Choose encryption (WPA/WEP/None) 4) Enter password 5) Generate and print. Guests can auto-connect without manual password entry.'
            },
            {
              q: language === 'zh' ? '二维码可以批量生成吗？' : 'Can I generate QR codes in bulk?',
              a: language === 'zh'
                ? '目前我们支持单个二维码生成，但你可以保存历史记录重复下载。对于大批量需求，建议使用我们的API服务或联系客服获取企业解决方案。'
                : 'Currently we support individual QR code generation with history saving for re-download. For bulk needs, consider our API service or contact us for enterprise solutions.'
            },
            {
              q: language === 'zh' ? '二维码的错误纠正级别如何选择？' : 'How to choose QR code error correction levels?',
              a: language === 'zh'
                ? '错误纠正级别选择：L级(7%)适合数字环境，M级(15%)适合一般用途，Q级(25%)适合工业环境，H级(30%)适合户外或可能损坏的场合。级别越高，容错性越好但尺寸越大。'
                : 'Error correction levels: L(7%) for digital use, M(15%) for general use, Q(25%) for industrial environments, H(30%) for outdoor or potentially damaged situations. Higher levels offer better error tolerance but larger size.'
            }
          ].map((faq, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden">
              <button
                onClick={() => toggleFaq(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <span className="text-white font-medium">{faq.q}</span>
                {openFaqs.includes(index) ? (
                  <ChevronUp className="w-5 h-5 text-white" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white" />
                )}
              </button>
              {openFaqs.includes(index) && (
                <div className="px-6 pb-4">
                  <p className="text-gray-300 leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default SEOContent; 